import { Field, Formik } from 'formik';
import i18next from 'i18next';
import React, { useState, FC, useEffect, useMemo } from 'react';
import { Alert, Button, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { deAT } from 'date-fns/locale';
import { useAuthFetchFromRestAPI } from '../../util/api';
import { AppointmentDto } from '../../../../shared/dtos/appointment.dto';

type DurationMin = 15 | 30 | 45 | 60;

type CreateAppointmentPayload = {
  start: string;
  end: string;
  duration: DurationMin;
}

type Slot = {
  start: string;
  end: string;
  displayStart: string;
  displayEnd: string;
  isBooked: boolean;
}

type Props = {
  therapy: any,
  onCreateAppointment: (appointment: CreateAppointmentPayload) => Promise<void>;
};
export const CreateAppointmentForm: FC<Props> = ({ therapy, onCreateAppointment }) => {
  const { authFetch } = useAuthFetchFromRestAPI();

  const [isLoading, setIsLoading] = useState(false);
  const [displayDayPicker, setDisplayDayPicker] = useState(false);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [selectedDate, setSelectedDate] = useState(DateTime.now().toFormat('yyyy-MM-dd'));
  const [selectedDuration, setDuration] = useState<DurationMin>(15);

  const today = useMemo(() => new Date(), []);
  const twoMonthDate = useMemo(() => DateTime.now().plus({ months: 2 }).toJSDate(), []);

  const isOverlapping = (aS: string, aE: string, bS: string, bE: string) => {
    const aStart = DateTime.fromISO(aS, { zone: 'utc' });
    const aEnd = DateTime.fromISO(aE, { zone: 'utc' });
    const bStart = DateTime.fromISO(bS, { zone: 'utc' });
    const bEnd = DateTime.fromISO(bE, { zone: 'utc' });
    return aStart.toMillis() < bEnd.toMillis() && aEnd.toMillis() > bStart.toMillis();
  };

  const buildSlotsForDay = (
    date: string,
    durationMin: DurationMin,
    appointments: { startDateTime: string; endDateTime: string }[]
  ): Slot[] => {
    const startLocal = DateTime.fromFormat(`${date}T06:00`, "yyyy-MM-dd'T'HH:mm");
    const endLocal = DateTime.fromFormat(`${date}T22:00`, "yyyy-MM-dd'T'HH:mm");

    const result: Slot[] = [];
    let cursor = startLocal;

    while (cursor.toMillis() < endLocal.toMillis()) {
      const slotEnd = cursor.plus({ minutes: durationMin });
      const start = cursor.toUTC().toISO() ?? '';
      const end = slotEnd.toUTC().toISO() ?? '';

      if (slotEnd.toMillis() > endLocal.toMillis()) {
        break;
      }

      const isBooked = appointments.some(a => isOverlapping(a.startDateTime, a.endDateTime, start, end));

      result.push({
        start,
        end,
        displayStart: cursor.toFormat('HH:mm'),
        displayEnd: slotEnd.toFormat('HH:mm'),
        isBooked,
      });

      cursor = cursor.plus({ minutes: 15 });
    }
    return result;
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      setIsLoading(true);

      try {
        const appointments: AppointmentDto[] = await authFetch('/api/v1/appointment/therapist');
        const list: { startDateTime: string; endDateTime: string }[] = Array.isArray(appointments) ? appointments : [];

        const dayAppointments = list.filter(a =>
          DateTime.fromISO(a.startDateTime, { zone: 'utc' }).toLocal().toFormat('yyyy-MM-dd') ===
          DateTime.fromFormat(selectedDate, 'yyyy-MM-dd').toFormat('yyyy-MM-dd'));

        const slots = buildSlotsForDay(selectedDate, selectedDuration, dayAppointments);

        if (!cancel) {
          setSlots(slots);
        }
      } catch (error) {
        console.error('Error loading appointments', error);
        if (!cancel) {
          setSlots([]);
        }
      } finally {
        if (!cancel) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancel = true;
    };
  }, [authFetch, selectedDate, selectedDuration, therapy]);

  const initialValues = {
    dateDisplay: DateTime.fromFormat(selectedDate, 'yyyy-MM-dd').toFormat('dd-MM-yyyy'),
    start: '',
    end: '',
    duration: selectedDuration as number,
  };

  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={initialValues}
      onSubmit={async ({ start, end, duration }, { setSubmitting }) => {
        try {
          if (!start || !end) {
            return;
          }

          const durNumber = Number(duration) as DurationMin;
          await onCreateAppointment({ start, end, duration: durNumber });
        } finally {
          setSubmitting(false);
        }
      }}
      validate={({ dateDisplay, start, end }) => {
        const errors: { dateDisplay?: string; start?: string, end?: string } = {};

        const dateSelection = DateTime.fromFormat(dateDisplay, 'dd-MM-yyyy');
        if (!dateSelection.isValid) {
          errors.dateDisplay = i18next.t('createAppointmentForm.validation.date');
        } else {
          const min = DateTime.now().startOf('day');
          const max = DateTime.now().plus({ months: 2 }).endOf('day');

          if (dateSelection.toMillis() < min.toMillis()) {
            errors.dateDisplay = i18next.t('trainingPlan.validation.pastDate');
          }

          if (dateSelection.toMillis() > max.toMillis()) {
            errors.dateDisplay = i18next.t('trainingPlan.validation.futureDate');
          }
        }

        if (!start) {
          errors.start = i18next.t('createAppointmentForm.validation.startTime');
        }

        if (!end) {
          errors.end = i18next.t('createAppointmentForm.validation.endTime');
        }

        if (!errors.start && !errors.end) {
          const st = DateTime.fromISO(start, { zone: 'utc' });
          const en = DateTime.fromISO(end, { zone: 'utc' });

          if (!st.isValid) {
            errors.start = i18next.t('createAppointmentForm.validation.startTime');
          }

          if (!en.isValid) {
            errors.end = i18next.t('createAppointmentForm.validation.endTime');
          }

          if (st.isValid && en.isValid) {
            if (en.toMillis() <= st.toMillis()) {
              errors.end = i18next.t('createAppointmentForm.validation.startTimeEndTime');
            }

            if (st.toMillis() <= DateTime.utc().toMillis()) {
              errors.start = i18next.t('createAppointmentForm.validation.pastTime');
            }
          }
        }

        return errors;
      }}
    >
      {({ values, errors, dirty, isValid, isSubmitting, handleSubmit, setFieldValue }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <Form.Group controlId="date">
            <Form.Label column={false}>{i18next.t('createAppointmentForm.labels.date')}</Form.Label>
            <Field name="dateDisplay">
              {({ field }) => (
                <>
                  <InputGroup className="mb-3" onClick={() => setDisplayDayPicker(!displayDayPicker)}>
                    <Form.Control disabled value={field.value} />
                    <Button
                      variant="dark"
                      className="pt-1 pe-3 ps-3 pb-0 rounded-0"
                      onClick={(event) => {
                        event.stopPropagation(); setDisplayDayPicker(!displayDayPicker);
                      }}>
                      <i className="fas fa-calendar" />
                    </Button>
                  </InputGroup>
                  { displayDayPicker && (
                    <DayPicker
                      mode="single"
                      numberOfMonths={1}
                      startMonth={today}
                      endMonth={twoMonthDate}
                      disabled={[{ before: today, after: twoMonthDate }]}
                      locale={deAT}
                      onDayClick={(day) => {
                        const selected = DateTime.fromJSDate(day).toFormat('yyyy-MM-dd');
                        setSelectedDate(selected);
                        setFieldValue('dateDisplay', DateTime.fromJSDate(day).toFormat('dd-MM-yyyy'));
                        setFieldValue('start', '');
                        setFieldValue('end', '');
                        setDisplayDayPicker(false);
                      }}
                    />
                  )}
                  {errors.dateDisplay && (
                    <div className="w-100 mt-1 text-danger" style={{ fontSize: '80%' }}>{errors.dateDisplay}</div>
                  )}
                </>
              )}
            </Field>
          </Form.Group>

          <Form.Group controlId="duration">
            <Form.Label>{i18next.t('createAppointmentForm.labels.selectDuration')}</Form.Label>
            <div className="mb-3 ms-2">
              {[15, 30, 45, 60].map((dur) => (
                <Form.Check
                  key={dur}
                  inline
                  type="radio"
                  name="durationGroup"
                  label={`${dur} ${i18next.t('createAppointmentForm.labels.min')}`}
                  checked={selectedDuration === dur}
                  onChange={() => {
                    setDuration(dur as DurationMin);
                    setFieldValue('start', '');
                    setFieldValue('end', '');
                  }}
                />
              ))}
            </div>
          </Form.Group>

          <Form.Group controlId="slots">
            <Form.Label>{i18next.t('createAppointmentForm.labels.startTime')}</Form.Label>
            <div id="create-appointment-form" className="overflow-auto overflow-x-hidden" style={{ maxHeight: '47vh' }}>
              <Row className="me-3 ms-0">
                {isLoading ? (<Col md={12}>Loading...</Col>) : (
                  <Col md={11} className="p-0">
                    {slots.length > 0 ? (
                      <Row className="nopadding">
                        {slots.map((slot, idx) => {
                          const active = values.start === slot.start;
                          return (
                            <Col md={3} xs={4} key={`slot-${idx}`} className="pe-0">
                              <Button
                                size="sm"
                                className="m-1 form-control p-0 bold-border-2"
                                disabled={slot.isBooked}
                                title={slot.isBooked
                                  ? i18next.t('createAppointmentForm.labels.bookedTooltip')
                                  : `${slot.displayStart} - ${slot.displayEnd}`}
                                variant={slot.isBooked ? 'outline-dark' : active ? 'primary' : 'outline-primary'}
                                onClick={async () => {
                                  await setFieldValue('start', slot.start);
                                  await setFieldValue('end', slot.end);
                                }}
                              >
                                {slot.displayStart}
                              </Button>
                            </Col>
                          );
                        })}
                      </Row>
                    ) : (
                      <Col md={12}>{i18next.t('createAppointmentForm.labels.noAvailableTime')}</Col>
                    )}
                  </Col>
                )}
              </Row>
            </div>
          </Form.Group>

          {values.start && values.end && (
            <Alert variant="dark">
              <span>
                {values.dateDisplay}{' '}
                {i18next.t('createAppointmentForm.labels.selectedTimeAt')}{' '}
                {DateTime.fromISO(values.start, { zone: 'utc' }).toLocal().toFormat('HH:mm')} - {DateTime.fromISO(values.end, { zone: 'utc' }).toLocal().toFormat('HH:mm')}
              </span>
            </Alert>
          )}

          {errors.start && (
            <div className="w-100 mt-1 text-danger" style={{ fontSize: '80%' }}>{errors.start}</div>
          )}

          <Button type="submit" disabled={!dirty || !isValid || isSubmitting || !values.start || !values.end}>
            {i18next.t('createAppointmentForm.labels.submit')}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default CreateAppointmentForm;
